<template>
    <div
        :class="classInstagramWithContent"
        class="instagram-with-content"
    >
        <div class="content-wrapper">
            <div
                v-if="data.optionalContent?.length"
                class="content"
            >
                <div class="content-items">
                    <ContentComponentItem
                        v-for="(component, key) in data.optionalContent"
                        :key="key"
                        :data="component"
                        :has-eager-image="hasEagerImage"
                        :page-type="pageType"
                    />
                </div>
            </div>

            <div class="instagram-post">
                <div
                    v-if="data.useImage"
                    class="instagram-post-uploaded"
                >
                    <CmsImage
                        v-if="data.instagramImage"
                        :data="data.instagramImage"
                        :is-eager="hasEagerImage"
                    />

                    <a
                        v-if="data.instagramLink && data.instagramUsername"
                        :href="data.instagramLink"
                        rel="nofollow"
                        target="_blank"
                        aria-label="Instagram - opens in a new tab"
                        >{{ data.instagramUsername }}</a
                    >
                </div>
                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-else
                    class="instagram-post-embedded"
                    v-html="localizeHrefs(data.instagramEmbedCode || '')"
                ></div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type {
    Color,
    ComponentInstagramWithContentFragment,
} from '@/graphql/api'
import type { ButtonColors } from '@/components/ui/buttons'
import { getColorForBackground } from '@/components/ui/buttons'
import { cmsColorToButtonColor } from '@/components/cms'
import type { Colors } from '@/components/ui'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import { useHrefLocalizer } from '@/router/href-localizer'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    data: ComponentInstagramWithContentFragment
    background: Color
    hasEagerImage?: boolean
    pageType: PageType
}>()

const localizeHrefs = useHrefLocalizer()

const backgroundColor = computed(
    (): ButtonColors => cmsColorToButtonColor(props.background)
)

const color = computed(
    (): Colors => getColorForBackground(backgroundColor.value)
)

const classInstagramWithContent = computed(() => ({
    [props.data.instagramWithContentWidth.toLocaleLowerCase()]: true,
    [`${props.data.instagramPosition.toLocaleLowerCase()}-image`]: true,
    'no-content': props.data.optionalContent?.length === 0,
}))
</script>

<style lang="scss" scoped>
.instagram-with-content {
    color: v-bind(color);
    padding: 20px;

    @include min-width(768) {
        max-width: 680px;
        margin: 0 auto;
        padding: 20px 0;
    }

    @include min-width(1024) {
        width: 100%;
        max-width: 1140px;
        padding: 40px 20px;
    }

    @include min-width(1140) {
        padding: 40px 0;
    }

    &.no-content {
        .content-wrapper {
            justify-content: center;
        }
    }

    &.wide {
        @include min-width(1024) {
            max-width: 1200px;
        }

        .content-wrapper {
            @include min-width(1024) {
                max-width: 1200px;
            }

            .instagram-post {
                width: 350px;

                @include min-width(1024) {
                    width: 400px;
                }
            }
        }
    }

    .content-wrapper {
        width: 100%;
        max-width: 1140px;
        margin: 0 auto;

        @include min-width(768) {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
        }

        @include min-width(1024) {
            align-items: center;
        }

        .content {
            width: 100%;

            @include min-width(768) {
                width: calc(50% - 20px);
                flex-shrink: 0;
            }

            @include min-width(1140) {
                width: 540px;
            }
        }

        .instagram-post {
            width: 100%;
            max-width: 100%;
            margin: 40px 0;
            display: flex;
            justify-content: center;

            @include min-width(768) {
                width: calc(50% - 40px);
                flex-shrink: 0;
                margin: 0;
            }

            @include min-width(1140) {
                width: 540px;
            }

            .instagram-post-embedded {
                width: 100%;
                display: flex;
                justify-content: center;
            }

            .instagram-post-uploaded {
                a {
                    font-family: $effra-font-stack;
                    line-height: 1em;
                    font-size: 0.8rem;
                    letter-spacing: 0.05em;
                }
            }
        }
    }

    &.left-image {
        .content-wrapper {
            flex-direction: row-reverse;
        }
    }
}
</style>
